import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AvailabilityPoll } from "@/components/AvailabilityPoll";
import { TrainingAttendanceTemplate } from "@/components/TrainingAttendanceTemplate";
import { CancellationTemplate } from "@/components/CancellationTemplate";
import { TemplateHub } from "@/components/TemplateHub";
import { LeagueGameReminders } from "@/components/LeagueGameReminders";
import { RemindersOverview } from "@/components/RemindersOverview";
import { SectionGameReminders } from "@/components/SectionGameReminders";
import { TeamNotifications } from "@/components/TeamNotifications";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, MessageCircle, Save, Send, RefreshCw, Loader2, CheckCircle,
  XCircle, AlertTriangle, Clock, BanIcon, Megaphone,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_VARS = [
  { key: "parent_name", label: "Parent name" },
  { key: "player_name", label: "Player name" },
  { key: "team_name", label: "Team name" },
  { key: "session_date", label: "Session date" },
  { key: "session_time", label: "Session time" },
  { key: "location", label: "Location" },
  { key: "location_block", label: "Location (auto-prefixed 'at ')" },
];

const HOUR_OPTIONS = [48, 24, 12, 6, 4, 2, 1];

function HoursBeforeSelector({ value = [], onChange, testidPrefix }) {
  const toggle = (h) => {
    if (value.includes(h)) onChange(value.filter((v) => v !== h));
    else onChange([...value, h].sort((a, b) => b - a));
  };
  return (
    <div className="flex flex-wrap gap-2">
      {HOUR_OPTIONS.map((h) => {
        const active = value.includes(h);
        return (
          <Button
            key={h}
            type="button"
            size="sm"
            variant={active ? "default" : "outline"}
            onClick={() => toggle(h)}
            className={
              active
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "border-slate-600 text-slate-300"
            }
            data-testid={`${testidPrefix}-hour-${h}`}
          >
            {h}h
          </Button>
        );
      })}
    </div>
  );
}

function TemplateEditor({ value, onChange, testid }) {
  const insertVar = (varKey) => {
    const ins = `{${varKey}}`;
    const el = document.getElementById(testid);
    if (el) {
      const start = el.selectionStart ?? (value || "").length;
      const end = el.selectionEnd ?? start;
      const next = (value || "").slice(0, start) + ins + (value || "").slice(end);
      onChange(next);
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(start + ins.length, start + ins.length);
      }, 0);
    } else {
      onChange((value || "") + ins);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {AVAILABLE_VARS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => insertVar(v.key)}
            className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-emerald-300 hover:bg-slate-700"
            data-testid={`${testid}-insert-${v.key}`}
          >
            + {v.label}
          </button>
        ))}
      </div>
      <Textarea
        id={testid}
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="bg-slate-800 border-slate-700 text-white"
      />
    </div>
  );
}

const STATUS_BADGE = {
  queued: "bg-slate-500/20 text-slate-300",
  sending: "bg-blue-500/20 text-blue-300",
  sent: "bg-blue-500/20 text-blue-300",
  accepted: "bg-blue-500/20 text-blue-300",
  delivered: "bg-emerald-500/20 text-emerald-300",
  read: "bg-emerald-500/20 text-emerald-300",
  failed: "bg-red-500/20 text-red-300",
  undelivered: "bg-red-500/20 text-red-300",
};

export default function AdminWhatsAppSettings({ user }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [optOuts, setOptOuts] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testMessage, setTestMessage] = useState("");
  const [testTemplate, setTestTemplate] = useState("free");
  const [tab, setTab] = useState("credentials");
  const [senders, setSenders] = useState(null);
  const [sendersLoading, setSendersLoading] = useState(false);
  const failed = messages.filter((m) => ["failed", "undelivered"].includes(m.status));
  const failedCount = failed.length;
  const failedHelp = [...new Set(failed.map((m) => m.error_help || m.error).filter(Boolean))].slice(0, 3);

  // Broadcast state
  const [teams, setTeams] = useState([]);
  const [sectionTeams, setSectionTeams] = useState([]);
  const [health, setHealth] = useState(null);
  const [broadcastTarget, setBroadcastTarget] = useState("team");
  const [broadcastTeamId, setBroadcastTeamId] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastCustomPhones, setBroadcastCustomPhones] = useState("");
  const [broadcastPreview, setBroadcastPreview] = useState(null);
  const [broadcastSending, setBroadcastSending] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, m, o, t, sec, h] = await Promise.all([
        axios.get(`${API}/admin/whatsapp/settings`, { withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/messages?limit=50`, { withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/opt-outs`, { withCredentials: true }),
        axios.get(`${API}/academy/teams`, { withCredentials: true }),
        axios.get(`${API}/section/whatsapp/settings`, { withCredentials: true })
          .catch(() => ({ data: { teams: [] } })),
        axios.get(`${API}/admin/whatsapp/inbound-health`, { withCredentials: true })
          .catch(() => ({ data: null })),
      ]);
      setHealth(h.data);
      setSettings(s.data);
      setMessages(m.data || []);
      setOptOuts(o.data || []);
      setTeams(t.data || []);
      setSectionTeams(sec.data?.teams || []);
      if (!testTo && s.data?.test_number) setTestTo(s.data.test_number);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load WhatsApp settings");
    } finally {
      setLoading(false);
    }
  };

  const loadSenders = async () => {
    setSendersLoading(true);
    try {
      const r = await axios.get(`${API}/admin/whatsapp/senders`, { withCredentials: true });
      setSenders(r.data);
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success("Sender looks good");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reach Twilio");
    } finally {
      setSendersLoading(false);
    }
  };

  const updateField = (path, value) => {
    setSettings((prev) => {
      const next = { ...prev };
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...(cur[keys[i]] || {}) };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload = { ...settings };
      // Don't resend the masked token — backend preserves the existing one
      if (!payload.auth_token || payload.auth_token.startsWith("*")) delete payload.auth_token;
      delete payload.auth_token_masked;
      const res = await axios.put(`${API}/admin/whatsapp/settings`, payload, {
        withCredentials: true,
      });
      setSettings(res.data);
      toast.success("WhatsApp settings saved");
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const doTestSend = async () => {
    const to = (testTo || "").trim();
    if (!to) {
      toast.error("Enter a phone number to send a test");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(
        `${API}/admin/whatsapp/test-send`,
        { to, message: testMessage || undefined, use_template: testTemplate === "free" ? undefined : testTemplate },
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success("Test message sent (Twilio SID: " + res.data.twilio_sid + ")");
      } else {
        toast.error("Failed: " + res.data.error);
      }
      // Refresh messages
      const m = await axios.get(`${API}/admin/whatsapp/messages?limit=50`, {
        withCredentials: true,
      });
      setMessages(m.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const removeOptOut = async (phone) => {
    if (!confirm(`Re-enable WhatsApp reminders for ${phone}?`)) return;
    try {
      await axios.post(
        `${API}/admin/whatsapp/opt-ins`,
        { phone },
        { withCredentials: true }
      );
      toast.success("Opt-in recorded");
      const o = await axios.get(`${API}/admin/whatsapp/opt-outs`, { withCredentials: true });
      setOptOuts(o.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const buildBroadcastPayload = (dryRun) => {
    const payload = { target: broadcastTarget, message: broadcastMessage, dry_run: dryRun };
    if (broadcastTarget === "team" || broadcastTarget === "section_team") payload.team_id = broadcastTeamId;
    if (broadcastTarget === "custom") {
      payload.custom_phones = broadcastCustomPhones
        .split(/[\n,]/)
        .map((p) => p.trim())
        .filter(Boolean);
    }
    return payload;
  };

  const previewBroadcast = async () => {
    if (["team", "section_team"].includes(broadcastTarget) && !broadcastTeamId) {
      toast.error("Pick a team");
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/whatsapp/broadcast`,
        buildBroadcastPayload(true),
        { withCredentials: true }
      );
      setBroadcastPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastMessage.trim()) {
      toast.error("Enter a message");
      return;
    }
    if (["team", "section_team"].includes(broadcastTarget) && !broadcastTeamId) {
      toast.error("Pick a team");
      return;
    }
    const count = broadcastPreview?.recipient_count ?? null;
    const confirmMsg = count !== null
      ? `Send this WhatsApp message to ${count} recipient${count === 1 ? "" : "s"}?`
      : "Send this WhatsApp message now?";
    if (!confirm(confirmMsg)) return;
    setBroadcastSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/whatsapp/broadcast`,
        buildBroadcastPayload(false),
        { withCredentials: true }
      );
      const d = res.data;
      if (d.error === "whatsapp_disabled") {
        toast.error("WhatsApp is disabled - enable it in the Credentials tab first");
      } else {
        toast.success(`Broadcast sent to ${d.sent || 0} / ${d.recipient_count || 0} recipients${d.skipped ? ` (${d.skipped} skipped)` : ""}`);
      }
      setBroadcastPreview(null);
      // Refresh messages so the user sees them in the log tab
      const m = await axios.get(`${API}/admin/whatsapp/messages?limit=50`, { withCredentials: true });
      setMessages(m.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setBroadcastSending(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const configured = Boolean(
    settings.credentials_ready ||
    (settings.account_sid && (settings.auth_token_masked || settings.auth_token) && settings.from_number)
  );

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" className="text-white hover:text-blue-200" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-emerald-400" />
              WhatsApp Settings
            </h1>
            {settings.enabled ? (
              <Badge className="bg-emerald-500/20 text-emerald-300">Enabled</Badge>
            ) : (
              <Badge className="bg-slate-500/20 text-slate-300">Disabled</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={loadAll} variant="outline" size="sm" className="border-white/20 text-white hover:bg-slate-900/50" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 space-y-6 max-w-5xl">
        {!configured && (
          <Card className="p-4 bg-amber-500/10 border-amber-500/30" data-testid="not-configured-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div className="text-sm text-amber-200">
                <p className="font-semibold">Twilio credentials not configured yet.</p>
                <p className="text-amber-200/80 mt-1">
                  Get your Account SID + Auth Token from the Twilio Console (free sandbox available).
                  Paste them below, pick your sandbox number as the sender, then enable reminders.
                </p>
              </div>
            </div>
          </Card>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="whatsapp-tabs">
            <TabsTrigger value="credentials" data-testid="tab-credentials">Credentials</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders">Reminders</TabsTrigger>
            <TabsTrigger value="teams" data-testid="tab-teams">By Team</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="tab-broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
            <TabsTrigger value="games" data-testid="tab-games">Game Reminders</TabsTrigger>
            <TabsTrigger value="test" data-testid="tab-test">Test Send</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Message Log</TabsTrigger>
            <TabsTrigger value="optouts" data-testid="tab-optouts">Opt-outs</TabsTrigger>
          </TabsList>

          {/* ---- CREDENTIALS ---- */}
          <TabsContent value="credentials">
            <Card className="p-6 bg-slate-900/50">
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white font-semibold">Enable WhatsApp sending</Label>
                    <p className="text-xs text-slate-400 mt-1">Master switch. When off, no reminders are sent and test-send is rejected.</p>
                  </div>
                  <Switch
                    checked={!!settings.enabled}
                    onCheckedChange={(v) => updateField("enabled", v)}
                    data-testid="enabled-switch"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white font-semibold">Sandbox mode</Label>
                    <p className="text-xs text-slate-400 mt-1">Use Twilio sandbox before approved sender is ready. Recipients must first message your sandbox with the join code.</p>
                  </div>
                  <Switch
                    checked={!!settings.sandbox_mode}
                    onCheckedChange={(v) => updateField("sandbox_mode", v)}
                    data-testid="sandbox-switch"
                  />
                </div>

                {settings.sandbox_mode && (
                  <div>
                    <Label className="text-white text-sm">Sandbox join code</Label>
                    <p className="text-xs text-slate-400 mb-2">
                      e.g. <code className="bg-slate-800 px-1 rounded">join flying-horse</code> — recipients must send this from their phone to your sandbox number once.
                    </p>
                    <Input
                      value={settings.sandbox_join_code || ""}
                      onChange={(e) => updateField("sandbox_join_code", e.target.value)}
                      placeholder="join your-sandbox-phrase"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="sandbox-join-code"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-white text-sm">Twilio Account SID</Label>
                  <Input
                    value={settings.account_sid || ""}
                    onChange={(e) => updateField("account_sid", e.target.value)}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                    data-testid="account-sid-input"
                  />
                </div>

                <div>
                  <Label className="text-white text-sm flex items-center gap-2">
                    Twilio Auth Token
                    {settings.auth_token_masked && (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 text-xs">
                        Saved: {settings.auth_token_masked}
                      </Badge>
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={settings.auth_token || ""}
                    onChange={(e) => updateField("auth_token", e.target.value)}
                    placeholder={settings.auth_token_masked ? "Enter new token to replace" : "Paste auth token"}
                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                    data-testid="auth-token-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Never shown after saving. Paste a new token any time to rotate.
                  </p>
                </div>

                <div>
                  <Label className="text-white text-sm">From (Sender) WhatsApp number</Label>
                  <Input
                    value={settings.from_number || ""}
                    onChange={(e) => updateField("from_number", e.target.value)}
                    placeholder="whatsapp:+14155238886"
                    className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                    data-testid="from-number-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Sandbox: <code>+14155238886</code> — only reaches phones that have sent it the
                    join phrase. Production: your approved WhatsApp Business number.
                  </p>

                  <div className="mt-3 p-3 rounded border border-slate-700 bg-slate-950/60">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-white">Senders on this Twilio account</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-slate-700 text-slate-300 h-7"
                        onClick={loadSenders}
                        disabled={sendersLoading}
                        data-testid="check-senders-btn"
                      >
                        {sendersLoading
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <RefreshCw className="w-3 h-3 mr-1" />}
                        Check senders
                      </Button>
                    </div>
                    {senders?.warning && (
                      <p className="text-xs text-amber-300 mb-2" data-testid="sender-warning">
                        {senders.warning}
                      </p>
                    )}
                    <details className="mt-3" data-testid="real-sender-help">
                      <summary className="text-xs text-emerald-300 cursor-pointer hover:underline">
                        How do I register a real Football And More number?
                      </summary>
                      <ol className="text-xs text-slate-400 mt-2 space-y-1.5 list-decimal pl-4">
                        <li>
                          Twilio Console → <strong>Messaging → Senders → WhatsApp senders</strong> →
                          <strong> Create new sender</strong>.
                        </li>
                        <li>
                          Choose the number parents should see. It must <strong>not</strong> be in
                          use in the WhatsApp app — if it is, delete that WhatsApp account first.
                        </li>
                        <li>
                          Click <strong>Continue with Facebook</strong> and sign in as an admin of
                          the Football And More business. Pick the existing WhatsApp Business
                          Account rather than creating a second one.
                        </li>
                        <li>
                          Verify ownership with the one-time code Meta sends by SMS or call.
                        </li>
                        <li>
                          Set the display name to <strong>Football And More</strong> and the category
                          to Education or Sports, then submit.
                        </li>
                        <li>
                          Once it shows ONLINE here, press <em>use this</em> above, save, and send
                          yourself a test before enabling sending.
                        </li>
                      </ol>
                      <p className="text-[11px] text-slate-500 mt-2">
                        Until Meta business verification is done you can still message parents, but
                        only 250 new conversations a day.
                      </p>
                    </details>
                    {senders?.senders?.length > 0 ? (
                      <div className="space-y-1">
                        {senders.senders.map((sn) => (
                          <div
                            key={sn.number}
                            className="flex items-center gap-2 text-xs"
                            data-testid={`sender-row-${sn.number}`}
                          >
                            <span className="font-mono text-slate-200">{sn.number}</span>
                            <Badge
                              className={`text-[10px] ${
                                sn.status === "ONLINE"
                                  ? "bg-emerald-500/20 text-emerald-300"
                                  : "bg-slate-600/30 text-slate-300"
                              }`}
                            >
                              {sn.status}
                            </Badge>
                            {sn.display_name && <span className="text-slate-400">{sn.display_name}</span>}
                            {sn.is_shared_sandbox && (
                              <span className="text-amber-400/80">shared sandbox</span>
                            )}                            {sn.in_use && <span className="text-emerald-400">· in use</span>}
                            {!sn.in_use && (
                              <button
                                type="button"
                                className="text-slate-400 hover:text-emerald-300 underline"
                                onClick={() => updateField("from_number", sn.number)}
                                data-testid={`use-sender-${sn.number}`}
                              >
                                use this
                              </button>
                            )}
                          </div>
                        ))}
                        {senders.senders.some((sn) => sn.is_shared_sandbox) && (
                          <p className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-slate-800">
                            <strong className="text-slate-200">Want a message on your own phone
                            today?</strong> Use the shared sandbox: open WhatsApp on your phone and
                            send <code className="text-emerald-300">join your-phrase</code> to
                            +14155238886 (the exact phrase is in Twilio Console → Messaging → Try it
                            out → Send a WhatsApp message). Then press <em>use this</em> on the
                            sandbox row, save, and Test Send a plain message — it works for 24 hours
                            after you write to it, with no template needed.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">
                        Press Check senders to ask Twilio which numbers can send WhatsApp.
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-white text-sm">Default test number (for quick test-send)</Label>
                  <Input
                    value={settings.test_number || ""}
                    onChange={(e) => updateField("test_number", e.target.value)}
                    placeholder="+6591234567"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="test-number-input"
                  />
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* ---- REMINDERS ---- */}
          <TabsContent value="templates">
            <TemplateHub onGoToTab={setTab} />
          </TabsContent>

          <TabsContent value="reminders">
            <div className="mb-4">
              <RemindersOverview />
            </div>            <div className="mb-4">
              <SectionGameReminders />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Training */}
              <Card className="p-5 bg-slate-900/50" data-testid="training-reminder-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-white font-semibold">Academy Training Reminders</Label>
                    <p className="text-xs text-slate-400 mt-0.5">Sent to parents before each training session</p>
                  </div>
                  <Switch
                    checked={!!settings.training_reminder?.enabled}
                    onCheckedChange={(v) => updateField("training_reminder.enabled", v)}
                    data-testid="training-enabled-switch"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white text-sm">Send hours before session</Label>
                    <HoursBeforeSelector
                      value={settings.training_reminder?.hours_before || []}
                      onChange={(v) => updateField("training_reminder.hours_before", v)}
                      testidPrefix="training"
                    />
                    <p className="text-xs text-slate-500 mt-1">Pick one or more lead-times.</p>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Email / fallback wording</Label>
                    <TemplateEditor
                      value={settings.training_reminder?.template || ""}
                      onChange={(v) => updateField("training_reminder.template", v)}
                      testid="training-template"
                    />
                  </div>
                  <TrainingAttendanceTemplate
                    settings={settings}
                    updateField={updateField}
                    onSaved={loadAll}
                  />
                </div>
              </Card>

              <CancellationTemplate />

              {/* Game */}
              <Card className="p-5 bg-slate-900/50" data-testid="game-reminder-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-white font-semibold">Section Game Reminders</Label>
                    <p className="text-xs text-slate-400 mt-0.5">Sent to signed-up players before each game</p>
                  </div>
                  <Switch
                    checked={!!settings.game_reminder?.enabled}
                    onCheckedChange={(v) => updateField("game_reminder.enabled", v)}
                    data-testid="game-enabled-switch"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white text-sm">Send hours before game</Label>
                    <HoursBeforeSelector
                      value={settings.game_reminder?.hours_before || []}
                      onChange={(v) => updateField("game_reminder.hours_before", v)}
                      testidPrefix="game"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm">Message template</Label>
                    <TemplateEditor
                      value={settings.game_reminder?.template || ""}
                      onChange={(v) => updateField("game_reminder.template", v)}
                      testid="game-template"
                    />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* ---- BROADCAST ---- */}
          <TabsContent value="availability">
            <AvailabilityPoll teams={teams} />
          </TabsContent>

          <TabsContent value="games">
            <LeagueGameReminders />
          </TabsContent>

          {/* ---- BY TEAM ---- */}
          <TabsContent value="teams">
            <TeamNotifications />
          </TabsContent>


          <TabsContent value="broadcast">
            <Card className="p-6 bg-slate-900/50" data-testid="broadcast-card">
              <h3 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-emerald-400" />
                Send a WhatsApp blast
              </h3>
              <p className="text-sm text-slate-400 mb-5">
                Pick a target audience and fire a one-off message. Template variables <code className="bg-slate-800 px-1 rounded">{"{parent_name}"}</code> and <code className="bg-slate-800 px-1 rounded">{"{player_name}"}</code> are auto-substituted for Academy audiences; for Section audiences they become the member's name and their team.
              </p>

              <div className="space-y-4 max-w-2xl">
                <div>
                  <Label className="text-white text-sm">Target audience</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {[
                      { k: "team", label: "One team" },
                      { k: "all_academy", label: "All Academy parents" },
                      { k: "section_team", label: "One Section team" },
                      { k: "all_section", label: "All Section members" },
                      { k: "custom", label: "Custom phone list" },
                    ].map((t) => (
                      <Button
                        key={t.k}
                        type="button"
                        size="sm"
                        variant={broadcastTarget === t.k ? "default" : "outline"}
                        onClick={() => {
                          setBroadcastTarget(t.k);
                          setBroadcastPreview(null);
                        }}
                        className={
                          broadcastTarget === t.k
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "border-slate-600 text-slate-300"
                        }
                        data-testid={`broadcast-target-${t.k}`}
                      >
                        {t.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {broadcastTarget === "team" && (
                  <div>
                    <Label className="text-white text-sm">Team</Label>
                    <select
                      value={broadcastTeamId}
                      onChange={(e) => {
                        setBroadcastTeamId(e.target.value);
                        setBroadcastPreview(null);
                      }}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 text-white rounded-md text-sm"
                      data-testid="broadcast-team-select"
                    >
                      <option value="">Select team...</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} {t.age_group ? `(${t.age_group})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {broadcastTarget === "section_team" && (
                  <div>
                    <Label className="text-white text-sm">Section team</Label>
                    <select
                      value={broadcastTeamId}
                      onChange={(e) => {
                        setBroadcastTeamId(e.target.value);
                        setBroadcastPreview(null);
                      }}
                      className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 text-white rounded-md text-sm"
                      data-testid="broadcast-section-team-select"
                    >
                      <option value="">Select team...</option>
                      {sectionTeams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Goes to Section members with a mobile on file. Opt-outs are always respected.
                    </p>
                  </div>
                )}

                {broadcastTarget === "custom" && (
                  <div>
                    <Label className="text-white text-sm">Phone numbers (one per line or comma-separated)</Label>
                    <Textarea
                      value={broadcastCustomPhones}
                      onChange={(e) => {
                        setBroadcastCustomPhones(e.target.value);
                        setBroadcastPreview(null);
                      }}
                      rows={3}
                      placeholder="+6591234567&#10;+6598765432"
                      className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
                      data-testid="broadcast-custom-phones"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-white text-sm">Message</Label>
                  <Textarea
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    rows={4}
                    placeholder="Hi {parent_name}, training this Saturday is cancelled due to heavy rain. Stay safe!"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="broadcast-message-input"
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={previewBroadcast}
                    className="border-slate-600 text-slate-200 hover:bg-slate-800"
                    data-testid="broadcast-preview-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Preview recipients
                  </Button>
                  <Button
                    onClick={sendBroadcast}
                    disabled={broadcastSending || !broadcastMessage.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="broadcast-send-btn"
                  >
                    {broadcastSending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Blast
                  </Button>
                </div>

                {broadcastPreview && (
                  <Card className="p-4 bg-slate-800/50 border-slate-700" data-testid="broadcast-preview-card">
                    <p className="text-sm text-white font-semibold">
                      {broadcastPreview.recipient_count} recipient{broadcastPreview.recipient_count === 1 ? "" : "s"} matched
                    </p>
                    {broadcastPreview.sample?.length > 0 ? (
                      <ul className="mt-2 space-y-1 text-xs font-mono text-slate-300">
                        {broadcastPreview.sample.map((s, i) => (
                          <li key={i}>
                            {s.phone}
                            {s.player_name ? (
                              <span className="text-slate-500 ml-2">({s.player_name})</span>
                            ) : null}
                          </li>
                        ))}
                        {broadcastPreview.recipient_count > broadcastPreview.sample.length && (
                          <li className="text-slate-500">
                            + {broadcastPreview.recipient_count - broadcastPreview.sample.length} more…
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-xs text-amber-400 mt-1">
                        No recipients found. Check the target or team selection.
                      </p>
                    )}
                  </Card>
                )}

                {!settings?.enabled && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                    <p className="text-sm text-amber-300 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      WhatsApp is currently disabled. Previewing still works, but Send will be blocked until you enable it in the Credentials tab.
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* ---- TEST ---- */}
          <TabsContent value="test">
            <Card className="p-6 bg-slate-900/50">
              <h3 className="text-lg font-semibold text-white mb-3">Send a test WhatsApp message</h3>
              <p className="text-sm text-slate-400 mb-4">
                Sends directly using the saved credentials, and works even while the master switch
                is off — so you can check a message lands on your own phone before going live.
                A plain message only reaches someone who has written to the sender in the last
                24 hours; for a cold number, pick one of the approved templates below.
              </p>
              <div className="space-y-3 max-w-lg">
                <div>
                  <Label className="text-white text-sm">Recipient phone (E.164 or SG local)</Label>
                  <Input
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="+6591234567"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="test-to-input"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm">What to send</Label>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {[
                      { k: "free", label: "Plain message" },
                      { k: "trial", label: "Trial confirmation template" },
                      { k: "league", label: "Game reminder template" },
                    ].map((o) => (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => setTestTemplate(o.k)}
                        className={`px-3 py-1.5 text-xs rounded border transition-colors ${
                          testTemplate === o.k
                            ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-300"
                            : "border-slate-700 text-slate-300 hover:bg-slate-800"
                        }`}
                        data-testid={`test-template-${o.k}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={testTemplate === "free" ? "" : "opacity-40 pointer-events-none"}>
                  <Label className="text-white text-sm">Message (leave blank for default)</Label>
                  <Textarea
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    rows={3}
                    placeholder="Optional custom message"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="test-message-input"
                  />
                </div>
                <Button
                  onClick={doTestSend}
                  disabled={testing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="test-send-btn"
                >
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Test
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* ---- MESSAGES ---- */}
          <TabsContent value="messages">
            <Card className="p-6 bg-slate-900/50 mb-4" data-testid="inbound-health-card">
              <h3 className="text-lg font-semibold text-white mb-3">Replies coming back</h3>
              {!health ? (
                <p className="text-sm text-slate-400">Loading…</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="text-slate-300" data-testid="open-polls-count">
                      {health.open_polls} question(s) awaiting an answer
                    </span>
                    {health.rejected_total > 0 && (
                      <span className="text-red-300" data-testid="rejected-count">
                        {health.rejected_total} reply/replies rejected as unverified
                      </span>
                    )}
                  </div>
                  {health.rejected_total > 0 && (
                    <p className="text-xs text-amber-300">
                      Rejected replies mean WhatsApp reached the portal but the message could not be
                      verified — usually a wrong Twilio Auth Token. Check the token below.
                    </p>
                  )}
                  <div className="space-y-1">
                    {(health.inbound || []).slice(0, 6).map((m) => (
                      <p key={m.id} className="text-xs text-slate-400">
                        <span className="text-slate-200">{m.from}</span>{" — "}
                        “{m.body}”{" "}
                        <span className="text-slate-600">
                          {String(m.received_at || "").slice(0, 16).replace("T", " ")}
                        </span>
                      </p>
                    ))}
                    {!(health.inbound || []).length && (
                      <p className="text-xs text-slate-500">No replies received yet.</p>
                    )}
                  </div>
                </div>
              )}
            </Card>
            <Card className="p-6 bg-slate-900/50" data-testid="messages-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Recent WhatsApp messages</h3>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-slate-700 text-slate-300"
                    onClick={loadAll}
                    data-testid="refresh-message-log-btn"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Check delivery
                  </Button>
                  <Badge className="bg-slate-700 text-slate-200">{messages.length}</Badge>
                </div>
              </div>
              {failedHelp.length > 0 && (
                <div
                  className="mb-4 p-3 rounded border border-red-500/40 bg-red-500/10"
                  data-testid="delivery-failure-banner"
                >
                  <p className="text-sm text-red-200 font-semibold mb-1">
                    {failedCount} of these did not reach the phone
                  </p>
                  {failedHelp.map((h) => (
                    <p key={h} className="text-xs text-red-200/90">• {h}</p>
                  ))}
                </div>
              )}
              {messages.length === 0 ? (
                <p className="text-slate-400 text-sm">No messages yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sent</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Body</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {messages.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className="text-xs text-slate-400 whitespace-nowrap">
                            {m.sent_at ? new Date(m.sent_at).toLocaleString() : "-"}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{m.to}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs border-slate-600 text-slate-300">
                              {m.reminder_type || "-"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-xs ${STATUS_BADGE[m.status] || "bg-slate-500/20 text-slate-300"}`}>
                              {m.status === "delivered" && <CheckCircle className="w-3 h-3 inline mr-1" />}
                              {m.status === "failed" && <XCircle className="w-3 h-3 inline mr-1" />}
                              {m.status === "queued" && <Clock className="w-3 h-3 inline mr-1" />}
                              {m.status}
                            </Badge>
                            {(m.error_help || m.error) && (
                              <p
                                className="text-[10px] text-red-300 mt-1 max-w-xs"
                                title={m.error || ""}
                                data-testid={`message-error-${m.id}`}
                              >
                                {m.error_help || m.error}
                                {m.error_code ? ` (code ${m.error_code})` : ""}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-slate-300 max-w-sm truncate" title={m.body}>
                            {m.body}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* ---- OPT-OUTS ---- */}
          <TabsContent value="optouts">
            <Card className="p-6 bg-slate-900/50" data-testid="optouts-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <BanIcon className="w-5 h-5 text-red-400" />
                  Opted-out phone numbers
                </h3>
                <Badge className="bg-slate-700 text-slate-200">{optOuts.length}</Badge>
              </div>
              <p className="text-sm text-slate-400 mb-3">
                Recipients who replied "STOP" on WhatsApp. No reminders are sent to these numbers.
              </p>
              <p className="text-xs text-slate-500 mb-3" data-testid="optout-scope-note">
                WhatsApp opt-out applies to the whole number, not one team — a player
                who stops hears nothing from any of their teams. "Last message" shows
                what they had from us just before, so you can see what prompted it.
              </p>
              {optOuts.length === 0 ? (
                <p className="text-slate-400 text-sm">No opt-outs.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Who</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Teams</TableHead>
                      <TableHead>Last message</TableHead>
                      <TableHead>Opted out at</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {optOuts.map((o) => {
                      const active = !o.opted_back_in_at;
                      const trigger = o.last_message_before_optout || {};
                      return (
                        <TableRow key={o.phone}>
                          <TableCell data-testid={`optout-who-${o.phone}`}>
                            <div className="text-slate-200 text-sm">{o.name}</div>
                            {o.email && (
                              <div className="text-xs text-slate-500">{o.email}</div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono">{o.phone}</TableCell>
                          <TableCell data-testid={`optout-teams-${o.phone}`}>
                            {(o.teams || []).length ? (
                              <div className="flex flex-wrap gap-1">
                                {o.teams.map((t) => (
                                  <Badge key={t} className="bg-slate-700 text-slate-200 text-xs">
                                    {t}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs" data-testid={`optout-trigger-${o.phone}`}>
                            {trigger.team_name || trigger.kind ? (
                              <span className="text-slate-300">
                                {trigger.team_name || trigger.kind}
                              </span>
                            ) : (
                              <span className="text-slate-500">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {o.opted_out_at ? new Date(o.opted_out_at).toLocaleString() : "-"}
                          </TableCell>
                          <TableCell>
                            {active ? (
                              <Badge className="bg-red-500/20 text-red-300">Opted out</Badge>
                            ) : (
                              <Badge className="bg-emerald-500/20 text-emerald-300">Re-subscribed</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {active && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeOptOut(o.phone)}
                                className="border-emerald-600 text-emerald-400"
                                data-testid={`opt-in-${o.phone}`}
                              >
                                Re-enable
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
